"use client";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
import { highlightSx } from '~/app/versuri/VersuriSearchStyleHighlight';
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { getAutocompleteSuggestions } from "~/lib/actions/lyrics";
import { debounce } from "~/lib/debounce";
import { stripDiacritics } from "~/lib/slugify";

import Search from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/system";
import type { LyricsSearchResult } from "~/types";
import VersuriHighlight from "./VersuriHighlight";

interface VersuriSearchBoxProps {
  initialQuery?: string;
}

export default function VersuriSearchBox({
  initialQuery = "",
}: VersuriSearchBoxProps) {
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<LyricsSearchResult[]>([]);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const [emptySuggestions, setEmptySuggestions] = useState<LyricsSearchResult[]>([]);
  const router = useRouter();

  const smartGradient = keyframes`
    0% { background-position: 100% 100%; }
    100% { background-position: top 37.5% left 37.5%; }
  `;

  // Extract search query from URL if on search page
  useEffect(() => {
    if (pathname.startsWith("/versuri/cauta/")) {
      const pathParts = pathname.split("/");
      const searchTermIndex = pathParts.indexOf("cauta");
      if (searchTermIndex !== -1 && pathParts[searchTermIndex + 1]) {
        const encodedQuery = pathParts[searchTermIndex + 1];
        const decodedQuery = decodeURIComponent(encodedQuery).replace(
          /\+/g,
          " ",
        );
        setTimeout(() => setSearchQuery(decodedQuery), 0);
      }
    }
  }, [pathname]);

  // Debounced function to fetch autocomplete suggestions using Fuse.js server action
  const fetchSuggestions = useMemo(
    () =>
      debounce(async (query: string) => {
        try {
          if (!query.trim() && emptySuggestions.length > 0) {
            setSuggestions(emptySuggestions);
            return;
          }

          const results = await getAutocompleteSuggestions(query);
          if (!query.trim()) {
            setEmptySuggestions(results);
          }

          setSuggestions(results);
        } catch (error) {
          console.error("Error fetching suggestions:", error);
          setSuggestions([]);
        }
      }, 300),
    [emptySuggestions],
  );

  const handleSearch = (value: string | null) => {
    const query = value || searchQuery;
    if (query && query.trim().length > 0) {
      // Track search event for analytics
      trackEvent(AnalyticsEvents.VERSURI_SEARCH, {
        query: query.trim(),
        source: value ? "autocomplete" : "manual",
        queryLength: query.trim().length,
      });

      // Remove diacritics first, then replace spaces with + and encode special chars
      const normalized = stripDiacritics(query.trim());
      const encodedQuery = normalized
        .replace(/\s+/g, "+")
        .replace(/[^a-zA-Z0-9+]/g, (char) => encodeURIComponent(char));
      router.push(`/versuri/cauta/${encodedQuery}`);
    }
  };

  const inputRef = React.useRef<HTMLInputElement>(null);

  // Focus input when pressing Ctrl+Cmd+F (Mac) or Ctrl+F / Ctrl+K on other platforms
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Mac: metaKey is Cmd. Windows/Linux: ctrlKey is Ctrl.
      const isMac = navigator.platform.toUpperCase().includes("MAC");

      // Shortcut: Ctrl+Cmd+F specifically requested. We'll also support Ctrl+F as a fallback.
      const pressedCtrlCmdF = isMac ? (e.ctrlKey && e.metaKey && e.key.toLowerCase() === "f") : (e.ctrlKey && e.key.toLowerCase() === "f");

      if (pressedCtrlCmdF) {
        // Prevent browser default find-in-page
        e.preventDefault();
        inputRef.current?.focus();
        // Place cursor at end
        const el = inputRef.current;
        if (el) {
          const len = el.value?.length ?? 0;
          el.setSelectionRange(len, len);
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <Box
      sx={{
        flexGrow: 1, // 1. Wrapper fills all available space
        mx: { xs: 0, md: 3 },
        ml: { xs: 1 },
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <Backdrop open={suggestionsVisible} sx={{ zIndex: 1100 }} onClick={() => setSuggestionsVisible(false)} />
      <Autocomplete<LyricsSearchResult, false, false, true>
        freeSolo
        options={suggestionsVisible ? suggestions : []}
        inputValue={searchQuery}
        blurOnSelect
        clearOnEscape
        sx={{
          width: { xs: "100%", md: 200 },
          transition: "width 220ms ease",
          bgcolor: "color-mix(in srgb, var(--mui-palette-background-paper), transparent 90%)",
          "&:focus-within": {
            // width: "100%",
            bgcolor: "background.paper",
            boxShadow: suggestionsVisible ? 3 : 0,

            width: { xs: "80vw", md: "100%" },
            position: { xs: "fixed", md: "relative" },
            top: { xs: 0, md: "auto" },
            left: { xs: "10vw", md: 0 },
            right: { xs: "10vw", md: "auto" },
            ml: { xs: 0 },
            mr: { xs: 0 },
            justifyContent: { xs: "center", md: "flex-end" },
            zIndex: { xs: 1200 },
          },
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }
          },
          popper: {
            sx: {
            }
          }
        }}
        onFocus={() => {
          if (!searchQuery) {
            fetchSuggestions("");
          }
          setTimeout(() => {
            setSuggestionsVisible(true);
          }, 250);
        }}
        onBlur={() => {
          setSuggestionsVisible(false);
        }}
        onInputChange={(event, newValue, reason) => {
          if (event) {
            setSearchQuery(newValue);
            fetchSuggestions(newValue);
          }
        }}
        onChange={(event, value, reason) => {
          // inputRef.current?.blur();
          if (value) {
            if (typeof value === "string") {
              handleSearch(value);
            } else if (value.slug) {
              router.push(`/versuri/cantec/${encodeURIComponent(value.slug)}`);
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            inputRef={inputRef}
            {...params}
            placeholder="Caută versuri..."
            size="small"
            sx={{
              "& fieldset": {
                border: "none",
              },
              "& .MuiInputBase-root": {
                transition: "background 300ms ease",
              },
              "&:hover:not(:focus-within) .MuiInputBase-root": {
                animation: `${smartGradient} 2.1s linear infinite`,
                background: `linear-gradient(135deg,
                    color-mix(in srgb, var(--mui-palette-primary-main) 36%, transparent),
                    color-mix(in srgb, var(--mui-palette-secondary-main) 28%, transparent),
                    color-mix(in srgb, var(--mui-palette-primary-dark) 20%, transparent),
                    color-mix(in srgb, var(--mui-palette-secondary-light) 8%, transparent)
                  )`,
                backgroundSize: "800% 800%",
              },
            }}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: <>{params.slotProps.input.endAdornment}</>,
              },
            }}
          />
        )}
        renderOption={({ key, ...props }, o) => {
          return (
            <li key={o.slug} {...props}>
              <Box sx={{ display: "flex", flexDirection: "column", overflow: "hidden", width: "100%" }}>
                <Typography variant="body2" noWrap sx={{ display: 'flex', alignItems: 'center', ...highlightSx }}>
                  <Box component="span" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span dangerouslySetInnerHTML={{ __html: o.formattedName }} />
                  </Box>
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 1, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    — <span dangerouslySetInnerHTML={{ __html: o.formattedArtist }} />
                  </Box>
                </Typography>
                <VersuriHighlight snippet={o.formattedLyrics} decodedQuery={searchQuery} linesToPreview={1} />
              </Box>
            </li>
          );
        }}
        filterOptions={o => o}
        getOptionLabel={o => typeof o === "string" ? o : (o.slug || "")}
        getOptionKey={o => typeof o === "string" ? o : o.slug}
        closeText="Închide"
        clearText="Șterge"
      />
    </Box>
  );
}
